import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserApi } from "../api/UserApi";
import { toast } from "react-toastify";
import { handleApiError } from "../utils/handleApiError";
import { useFontenehusApi } from "../api/FontenehusApi";
import type { Fontenehus } from "../types/Fontenehus";

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fontenehusCode, setFontenehusCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { registerAccount } = useUserApi();
  const [guestInput, setGuestInput] = useState(false);
  const { getAllFontenehus } = useFontenehusApi();
  const [fontenehusList, setFontenehusList] = useState<Fontenehus[]>([]);
  const [selectedFontenehusId, setSelectedFontenehusId] = useState<
    number | null
  >(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirm) {
      setError("Passordene stemmer ikke overens.");
      return;
    }
    if (!fontenehusCode && !guestInput) {
      setError("Velg et Fontenehus før du fortsetter.");
      return;
    }

    setLoading(true);
    try {
      const response = await registerAccount(
        username,
        password,
        email,
        fontenehusCode,
        displayName,
        selectedFontenehusId,
      );
      if (response.status === 200) {
        toast.info(response.data.message);
        navigate("/");
      }
    } catch (err: any) {
      handleApiError(err, "Feil ved registrering.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchFontenehus = async () => {
      try {
        const response = await getAllFontenehus();
        setFontenehusList(response.data.data);
      } catch (err: any) {
        handleApiError(err, "Feil ved henting av fontenehus");
      }
    };

    fetchFontenehus();
  }, [getAllFontenehus]);

  useEffect(() => {
    setFontenehusCode("");
  }, [guestInput]);

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen bg-orange-50 overflow-hidden">
      <header className="absolute top-0 left-0 w-full flex justify-center py-4 bg-white/80 backdrop-blur-sm shadow-sm z-30">
        <img
          src="/assets/fontenehusetlogo.webp"
          alt="Fontenehuset Logo"
          className="w-40 sm:w-48 md:w-56 opacity-95 drop-shadow-md"
        />
      </header>

      <div className="flex flex-col items-center justify-center translate-y-[-4%]">
        <div className="relative z-20 bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-8 w-full max-w-md mx-4 border border-orangeBrand/20">
          <h1 className="text-3xl font-bold text-center text-orangeBrand mb-2">
            Opprett bruker
          </h1>
          <p className="text-center text-textDark/70 mb-6">
            Fyll inn detaljene dine for å komme i gang
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* Username */}
            <div>
              <label className="block text-textDark font-medium mb-1">
                Brukernavn
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full border border-orangeBrand/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
                placeholder="Skriv inn brukernavn"
                required
              />
            </div>
            {/* DisplayName */}
            <div>
              <label className="block text-textDark font-medium mb-1">
                Visningsnavn
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full border border-orangeBrand/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
                placeholder="Skriv inn visningsnavn"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-textDark font-medium mb-1">
                E-post
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-orangeBrand/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
                placeholder="navn@eksempel.no"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-textDark font-medium mb-1">
                Passord
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-orangeBrand/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
                placeholder="••••••••"
                required
              />
            </div>

            {/* Confirm password */}
            <div>
              <label className="block text-textDark font-medium mb-1">
                Gjenta passord
              </label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full border border-orangeBrand/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
                placeholder="••••••••"
                required
              />
            </div>
            {/* Fontenehus code input */}
            {!guestInput && (
              <div className="mb-4">
                <label className="block text-textDark font-medium mb-1">
                  Skriv inn Fontenehus-kode
                </label>
                <input
                  type="text"
                  value={fontenehusCode}
                  onChange={(e) => setFontenehusCode(e.target.value)}
                  className="w-full border border-orangeBrand/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
                  placeholder="Eks: ABC123"
                  required
                />
              </div>
            )}

            {/* Fontenehus selection (only if guest) */}
            {guestInput && (
              <div className="mb-4">
                <label className="block text-textDark font-medium mb-1">
                  Velg Fontenehus
                </label>
                <select
                  value={selectedFontenehusId || ""}
                  onChange={(e) =>
                    setSelectedFontenehusId(Number(e.target.value))
                  }
                  className="w-full border border-orangeBrand/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
                  required
                >
                  <option value="">Velg et Fontenehus...</option>
                  {fontenehusList.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Guest toggle */}
            <div className="mb-4 flex items-center gap-2">
              <input
                type="checkbox"
                id="anonymousSuggestions"
                checked={guestInput}
                onChange={(e) => setGuestInput(e.target.checked)}
                className="h-4 w-4 text-orangeBrand border-gray-300 rounded focus:ring-orangeBrand"
              />
              <label
                htmlFor="anonymousSuggestions"
                className="text-textDark font-medium"
              >
                Registrer som gjest
              </label>
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="bg-orangeBrand hover:bg-orangeHover text-white font-semibold py-2 rounded-lg transition mt-2"
            >
              {loading ? "Oppretter..." : "Opprett bruker"}
            </button>
          </form>

          <div className="mt-4 text-center text-sm text-textDark/80">
            <p>
              Allerede bruker?{" "}
              <button
                type="button"
                className="text-orangeBrand hover:text-orangeHover font-medium"
                onClick={() => navigate("/")}
              >
                Logg inn
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
